import { AgTechDataAction } from "@agtech/core/data/actions/AgTechDataActions";
import { useAgTechHttpSubmitEntityRequest, useAgTechHttpPostRequest, useAgTechHttpPostEntityRequest } from "@agtech/core/data/http/AgTechApiPostRequests";
import { Index } from "@agtech/core/utilities/AgTechUtilities";
import { useQueryClient } from "@tanstack/react-query";
import { SaleTaskTemplate, SaleTaskTemplateItem, TaskTemplateFormData } from "./TaskTemplateData";
import { TaskTemplateDataGetTemplatesQuery, TaskTemplateDataGetItemsQuery } from "./TaskTemplateQueries";
import { PlanningTaskDataGetSaleTasksQuery } from "../planningTasks/PlanningTaskQueries";

/// Templates + items, submitted together

export const useTaskTemplateWriteWithItemsAction = (): AgTechDataAction<TaskTemplateFormData, SaleTaskTemplate> => {
    // The add endpoint returns the created item wrapped in the backend's Result<T> envelope, which
    // plain useAgTechHttpPostRequest does NOT unwrap (only the ...EntityRequest variants strip the
    // envelope down to .data). The template submit needs the same treatment, since template.id is
    // read off the response below to scope the item calls that follow it.
    const templateSubmitRequest = useAgTechHttpSubmitEntityRequest<SaleTaskTemplate>();
    const addItemRequest = useAgTechHttpPostEntityRequest<SaleTaskTemplateItem>();
    const updateItemRequest = useAgTechHttpPostRequest<SaleTaskTemplateItem, boolean>();
    const deleteItemRequest = useAgTechHttpPostRequest<{}, boolean>();
    const queryClient = useQueryClient();

    return {
        name: 'WriteSaleTaskTemplateWithItems',
        getConfiguration: props => ({
            actionExecutionMessage: props.originalEntity?.template.id ? 'Updating task template...' : 'Creating task template...',
            actionConfirmationMessage: `Successfully ${props.originalEntity?.template.id ? 'updated' : 'created'} task template`
        }),
        action: async props => {
            const templateResponse = await templateSubmitRequest.post({
                path: 'SaleTaskTemplates',
                postedData: props.submittedEntity.template
            });

            if (!templateResponse.success) {
                return templateResponse;
            }

            const template = templateResponse.success.data;

            // Not run as a transaction - if an item call fails partway through, the template and
            // whatever items succeeded before it are still persisted. Acceptable here: worst case is
            // reopening the template to finish reconciling, not silent data loss.
            for (const item of props.submittedEntity.items) {
                if (item.id) {
                    await updateItemRequest.post({
                        path: `SaleTaskTemplates/${template.id}/Items/${item.id}`,
                        postedData: item
                    });
                }
                else {
                    await addItemRequest.post({
                        path: `SaleTaskTemplates/${template.id}/Items`,
                        postedData: { ...item, saleTaskTemplateId: template.id }
                    });
                }
            }

            for (const deletedItemId of props.submittedEntity.deletedItemIds) {
                await deleteItemRequest.post({
                    path: `SaleTaskTemplates/${template.id}/Items/${deletedItemId}/Delete`,
                    postedData: {}
                });
            }

            return templateResponse;
        },
        onSuccess: async props => {
            props.updateQueryData({
                query: TaskTemplateDataGetTemplatesQuery,
                update: templates => {
                    return {
                        ...templates,
                        [props.responseData.id]: props.responseData
                    };
                }
            });

            await queryClient.invalidateQueries({
                queryKey: TaskTemplateDataGetItemsQuery.queryKey({ templateId: props.responseData.id })
            });
        }
    }
}

export type TaskTemplateDeleteActionDTO = {
    template: SaleTaskTemplate
}

export const useTaskTemplateDeleteAction = (): AgTechDataAction<TaskTemplateDeleteActionDTO, boolean> => {
    const deleteTemplateHttpRequest = useAgTechHttpPostRequest<SaleTaskTemplate, boolean>();

    return {
        name: 'DeleteSaleTaskTemplate',
        getConfiguration: () => ({
            actionExecutionMessage: 'Deleting task template...',
            actionConfirmationMessage: 'Successfully deleted task template'
        }),
        confirm: async props => {
            return props.appContext.confirmations.askForConfirmation({
                content: {
                    header: 'Are you sure you want to delete this task template?',
                    details: 'All items in this template will be removed. Sales that already had this template applied are not affected.'
                }
            });
        },
        action: async props => {
            return await deleteTemplateHttpRequest.post({
                path: 'SaleTaskTemplates/Delete',
                postedData: props.submittedEntity.template
            });
        },
        onSuccess: async props => {
            props.updateQueryData({
                query: TaskTemplateDataGetTemplatesQuery,
                update: templates => {
                    const updatedTemplates = Object.values(templates)
                        .filter(t => t.id !== props.submittedEntity.template.id);

                    return Index(updatedTemplates, t => t.id);
                }
            });

            props.appContext.popups.closeAll();
        }
    }
}

/// Apply to sale

export type ApplyTaskTemplateActionDTO = {
    templateId: number,
    saleId: number
}

export const useApplyTaskTemplateToSaleAction = (): AgTechDataAction<ApplyTaskTemplateActionDTO, any> => {
    const applyRequest = useAgTechHttpPostRequest<any, any>();
    const queryClient = useQueryClient();

    return {
        name: 'ApplySaleTaskTemplateToSale',
        getConfiguration: () => ({
            actionExecutionMessage: 'Applying template to sale...',
            actionConfirmationMessage: 'Successfully applied the template to this sale'
        }),
        action: async props => {
            return await applyRequest.post({
                path: `SaleTaskTemplates/${props.submittedEntity.templateId}/ApplyToSale/${props.submittedEntity.saleId}`,
                postedData: {}
            });
        },
        onSuccess: async props => {
            // The response is the updated Sale, not the planning tasks index this query caches -
            // simplest to just invalidate and let it refetch, same as SaleRealtimeConnection does
            // for other server-driven changes rather than hand-merging a differently-shaped payload.
            await queryClient.invalidateQueries({
                queryKey: PlanningTaskDataGetSaleTasksQuery.queryKey({ saleId: props.submittedEntity.saleId })
            });
        }
    }
}
